import { useState, type FormEvent } from "react";
import { Plus, X } from "lucide-react";
import { api } from "../../api";
import { STAGES } from "../../crm";
import { OUTCOME_LABEL, type Drip, type DripDefaults, type DripTrigger, type FormOutcome } from "../../drips";
import Dialog from "../Dialog";
import type { CrmForm } from "./types";

type Settings = Pick<Drip, "name" | "from_name" | "reply_to" | "footer_address" | "triggers" | "stop_on_booking"> & { from_local: string };

/**
 * New drip, or Edit drip: its name, sender, who it starts for and when it stops, and the
 * mailing address. One form for both, like Magus's campaign editor. A new drip also takes
 * its first email here and can go live in the same step (with the people already in its
 * start stages), so a working drip is one dialog. Sender and address start filled in from
 * the last drip (defaults), never as grey examples that save empty.
 */
export default function DripSettingsDialog({ drip, forms, defaults, onClose, onSaved }: {
  drip: Drip | null;
  forms: CrmForm[];
  defaults?: DripDefaults;
  onClose: () => void;
  onSaved: (drip: Drip, created: boolean) => void;
}) {
  const [f, setF] = useState<Settings>(() => ({
    name: drip?.name || "",
    from_name: drip ? drip.from_name : defaults?.from_name || "",
    from_local: drip ? drip.from_local || "" : defaults?.from_local || "",
    reply_to: drip ? drip.reply_to : defaults?.reply_to || "",
    footer_address: drip ? drip.footer_address : defaults?.footer_address || "",
    triggers: drip?.triggers || [],
    stop_on_booking: drip?.stop_on_booking ?? true,
  }));
  // The No Limits domain drip email goes from, and the address an empty box keeps.
  const ownAddress = drip?.from_address || (defaults?.from_domain ? `${defaults.from_default_local || "notifications"}@${defaults.from_domain}` : "");
  const fromDomain = ownAddress.includes("@") ? ownAddress.slice(ownAddress.lastIndexOf("@") + 1) : defaults?.from_domain || "";
  const defaultLocal = drip ? (drip.from_local ? "notifications" : ownAddress.slice(0, ownAddress.lastIndexOf("@"))) : defaults?.from_default_local || "notifications";
  const [first, setFirst] = useState({ subject: "", body: "Hi {{first_name|there}},\n\n" });
  const [includeCurrent, setIncludeCurrent] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const set = (patch: Partial<Settings>) => setF((cur) => ({ ...cur, ...patch }));
  const setTrigger = (i: number, next: DripTrigger) => set({ triggers: f.triggers.map((t, k) => (k === i ? next : t)) });
  const liveForms = forms.filter((x) => x.status !== "archived");
  // A new rule starts on a form people can fill in (a live one), not a draft.
  const firstForm = forms.find((x) => x.status === "live") || liveForms[0];
  const stageNames = [...new Set(f.triggers.filter((t) => t.type === "stage").map((t) => STAGES.find((x) => x.id === (t as { stage: string }).stage)?.label || "that stage"))];

  // What still stops a new drip from going live, in plain words (the server checks the same).
  const firstBody = first.body.replace(/^Hi \{\{first_name\|there\}\},\s*$/, "").trim();
  const missing = !first.subject.trim() ? "Add a subject for the first email."
    : !firstBody ? "Write the first email."
      : !f.from_name.trim() ? "Add a From name."
        : !f.footer_address.trim() ? "Add your mailing address. The law requires it on every marketing email."
          : "";

  async function submit(e: FormEvent, goLive = false) {
    e.preventDefault();
    if (busy) return;
    if (goLive && missing) { setError(missing); return; }
    setBusy(true); setError("");
    let created: Drip | null = null;
    try {
      // A new drip starts as a draft with one empty email; the settings and first email then go on it.
      const base = drip || (created = await api<Drip>("/api/crm/drips", { method: "POST", body: JSON.stringify({ name: f.name.trim() || "New drip" }) }));
      const steps = drip
        ? base.steps.map((s) => ({ id: s.id ?? null, wait_days: s.wait_days, subject: s.subject, body: s.body }))
        : base.steps.map((s, i) => (i === 0 ? { id: s.id ?? null, wait_days: 0, subject: first.subject.trim(), body: firstBody ? first.body.trim() : "" } : s));
      let out = await api<Drip>(`/api/crm/drips/${base.id}`, { method: "PUT", body: JSON.stringify({ ...f, name: f.name.trim(), steps }) });
      if (goLive) {
        out = await api<Drip>(`/api/crm/drips/${base.id}/status`, { method: "POST", body: JSON.stringify({ status: "live", include_current: includeCurrent && stageNames.length > 0 }) });
      }
      onSaved({ ...base, ...out }, !drip);
    } catch (err) {
      // The drip exists even when a later step did not save: open it, so nothing is lost.
      if (created) { onSaved(created, true); return; }
      setError(err instanceof Error ? err.message : "The drip did not save.");
      setBusy(false);
    }
  }

  return (
    <Dialog title={drip ? "Edit drip" : "New drip"} onClose={() => { if (!busy) onClose(); }}>
      <form className="drip-form" onSubmit={(e) => void submit(e)}>
        <label>Drip name
          <input required maxLength={120} value={f.name} placeholder="Name this drip" onChange={(e) => set({ name: e.target.value })} />
        </label>
        <div className="drip-form-row">
          <label>From name
            <input maxLength={80} value={f.from_name} placeholder="Shawn at No Limits" onChange={(e) => set({ from_name: e.target.value })} />
          </label>
          <label>From address
            <span className="drip-address">
              <input maxLength={64} value={f.from_local} placeholder={defaultLocal} autoCapitalize="off" spellCheck={false}
                onChange={(e) => set({ from_local: e.target.value.replace(/\s+/g, "").replace(/@.*$/, "").toLowerCase() })} />
              {fromDomain && <span className="drip-address-domain">@{fromDomain}</span>}
            </span>
          </label>
        </div>
        <label>Replies go to
          <input type="email" maxLength={200} value={f.reply_to} placeholder="shawn@nolimits.marketing" onChange={(e) => set({ reply_to: e.target.value.trim() })} />
        </label>

        <fieldset className="drip-form-set">
          <legend>Who gets it</legend>
          {!f.triggers.length && <p className="drip-form-hint">Only people you add by hand.</p>}
          {f.triggers.length > 0 && (
            <ul className="drip-rules">
              {f.triggers.map((t, i) => (
                <li key={i}>
                  {t.type === "form" ? (
                    <>
                      <span>{t.outcome === "left" ? "Someone starts" : "Someone sends"}</span>
                      <select aria-label="Form" value={t.form_id} onChange={(e) => setTrigger(i, { ...t, form_id: Number(e.target.value) })}>
                        {!liveForms.some((x) => x.id === t.form_id) && <option value={t.form_id}>A deleted form</option>}
                        {liveForms.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
                      </select>
                      <select aria-label="Answer" value={t.outcome} onChange={(e) => setTrigger(i, { ...t, outcome: e.target.value as FormOutcome })}>
                        {(Object.keys(OUTCOME_LABEL) as FormOutcome[]).map((o) => <option key={o} value={o}>{OUTCOME_LABEL[o]}</option>)}
                      </select>
                    </>
                  ) : (
                    <>
                      <span>A deal moves to</span>
                      <select aria-label="Stage" value={t.stage} onChange={(e) => setTrigger(i, { type: "stage", stage: e.target.value })}>
                        {STAGES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
                      </select>
                    </>
                  )}
                  <button type="button" className="icon-button" aria-label="Remove this rule" onClick={() => set({ triggers: f.triggers.filter((_, k) => k !== i) })}><X aria-hidden="true" /></button>
                </li>
              ))}
            </ul>
          )}
          {f.triggers.length < 10 && (
            <div className="drip-form-add">
              <button type="button" className="button secondary small" disabled={!liveForms.length} title={liveForms.length ? undefined : "Add a form on the Forms tab first"}
                onClick={() => set({ triggers: [...f.triggers, { type: "form", form_id: firstForm.id, outcome: "not_fit" }] })}><Plus aria-hidden="true" />When a form is sent</button>
              <button type="button" className="button secondary small" onClick={() => set({ triggers: [...f.triggers, { type: "stage", stage: "follow_up" }] })}><Plus aria-hidden="true" />When a deal moves</button>
              <button type="button" className="button secondary small" disabled={!liveForms.length} title={liveForms.length ? undefined : "Add a form on the Forms tab first"}
                onClick={() => set({ triggers: [...f.triggers, { type: "form", form_id: firstForm.id, outcome: "left" }] })}><Plus aria-hidden="true" />When someone leaves a form</button>
            </div>
          )}
          <label className="drip-check"><input type="checkbox" checked={f.stop_on_booking} onChange={(e) => set({ stop_on_booking: e.target.checked })} />Stop when they book a call</label>
        </fieldset>

        <label>Mailing address
          <textarea rows={3} maxLength={400} value={f.footer_address} placeholder={"No Limits Marketing\nStreet address\nCity, State ZIP"} onChange={(e) => set({ footer_address: e.target.value })} />
          <span className="drip-form-hint">Shown at the bottom of every email. The law requires it.</span>
        </label>

        {!drip && (
          <fieldset className="drip-form-set">
            <legend>First email</legend>
            <label>Subject
              <input maxLength={200} value={first.subject} placeholder="Quick question about your next event" onChange={(e) => setFirst((c) => ({ ...c, subject: e.target.value }))} />
            </label>
            <label>Message
              <textarea rows={6} maxLength={20000} value={first.body} onChange={(e) => setFirst((c) => ({ ...c, body: e.target.value }))} />
              <span className="drip-form-hint">Goes out as soon as someone joins. {"{{first_name|there}}"} becomes their first name. Add more emails on the next page.</span>
            </label>
          </fieldset>
        )}

        {!drip && stageNames.length > 0 && (
          <label className="drip-check"><input type="checkbox" checked={includeCurrent} onChange={(e) => setIncludeCurrent(e.target.checked)} />When it goes live, also send it to people already in {stageNames.join(" or ")}</label>
        )}

        {error && <p className="crm-error" role="alert">{error}</p>}
        <div className="operation-actions">
          <button type="button" className="button secondary" disabled={busy} onClick={onClose}>Cancel</button>
          {drip ? (
            <button type="submit" className="button" disabled={busy || !f.name.trim()}>{busy ? "Saving…" : "Save drip"}</button>
          ) : (
            <>
              <button type="submit" className="button secondary" disabled={busy || !f.name.trim()}>{busy ? "Saving…" : "Save as draft"}</button>
              <button type="button" className="button" disabled={busy || !f.name.trim()} title={missing || undefined} onClick={(e) => void submit(e as unknown as FormEvent, true)}>Create and go live</button>
            </>
          )}
        </div>
      </form>
    </Dialog>
  );
}
